import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useDispatch } from '@tmapy/redux';
import { useMessage } from '@tmapy/intl';

import {
  DangerAlert,
  WaitingAlert,
  FormRow,
  FormItem,
  BtnBar,
  CtaBtn,
  Input,
  Form,
  SuccessAlert,
  Textarea,
  SecondaryBtn,
} from '@tmapy/style-guide';

import {
  useAppContext,
  useAuth,
  useFormItemServerErrors,
  useHistoryAction,
  useInput,
  useSelector,
  useAction,
  toBtnQueryState,
} from '../../hooks';
import { InputProps } from '../../hooks/useInput';
import { MainContent } from '../../app/components/MainContent';
import { PageTitle } from '../../app/components/PageTitle';
import { QUERY_STATE } from '../../constants';

import { actionResourceInvalidate } from '../../redux/resource/actions';
import {
  OrganizationsRetrieveResponse,
  useOrganizationsRetrieveResource,
} from '../../openapi/operations/organizationsRetrieve';
import { organizationsPartialUpdate } from '../../openapi/operations/organizationsPartialUpdate';
import { getFormValues } from '../../docs/utils/getformvalues';
import { AddressAutocomplete } from '../../app/components/AddressAutocomplete';
import { organizationsDestroy } from '../../openapi/operations/organizationsDestroy';
import { organizationsListOperation } from '../../openapi/operations/organizationsList';
import { organizationsCreate } from '../../openapi/operations/organizationsCreate';
import { organizationFormMessages, organizationMessages } from './organizationMessages';

type DetailProps = {
  organizationId?: string;
};

export const OrganizationDetail: React.FC<DetailProps> = React.memo((props) => {
  const auth = useAuth();
  const dispatch = useDispatch();
  const formatMessage = useMessage();

  const { appType } = useAppContext();
  const [validate, setValidate] = useState(false);
  const [saveQueryState, setSaveQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [deleteQueryState, setDeleteQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);

  const serverErrors = useSelector((state) => state.docedit.errors);
  const formItemServerErrors = useFormItemServerErrors(serverErrors);

  const organizationId = props.organizationId ? parseInt(props.organizationId, 10) : undefined;

  const resource = useOrganizationsRetrieveResource(organizationId ? { id: organizationId } : null);
  const data = useMemo(() => {
    return (resource.data ? { ...resource.data } : {}) as OrganizationsRetrieveResponse;
  }, [resource.data]);

  // ACL
  const acl = useMemo(
    () => ({
      /**
       * update a partial_update je nutno mit nastaveno stejne (FE,BE), ukladani vzdy jde pres PATCH tedy PartialUpdate
       * partial_update urcuje form disable jednotlivych polozek
       * update urcuje viditelnost ukladaciho tlacitka
       */
      canUpdate: auth.hasPermission('Organizations', 'partial_update', data),
      canDestroy: auth.hasPermission('Organizations', 'destroy', data),
      organizationUpdate: auth.hasPermission('Organizations', 'update', data),
    }),
    [auth, data],
  );

  const routeList = useHistoryAction('prejimkaOrganizationList', { appType });
  const routeDetail = useHistoryAction('prejimkaOrganization', { id: organizationId! });

  // HOOK FOR INPUTS
  const formItemChangeCallback = useCallback(() => {
    setSaveQueryState(QUERY_STATE.EMPTY);
  }, [setSaveQueryState]);

  // NAME
  const name = useInput({
    name: 'name',
    isDisabled: !acl.canUpdate,
    initialValue: data.name,
    validate: validate,
    validator: (value) => (!value ? formatMessage(organizationFormMessages.nameError) : null),
    isRequired: true,
    autoFocus: !organizationId,
    label: formatMessage(organizationMessages.name),
    onChange: formItemChangeCallback,
    ...formItemServerErrors.name,
  });

  // ICO
  const ico = useInput({
    name: 'ico',
    isDisabled: !acl.canUpdate,
    initialValue: data.ico,
    validate: validate,
    label: formatMessage(organizationMessages.ico),
    onChange: formItemChangeCallback,
    ...formItemServerErrors.ico,
  });

  // DIC
  const dic = useInput({
    name: 'dic',
    isDisabled: !acl.canUpdate,
    initialValue: data.dic,
    validate: validate,
    validator: (value) =>
      value.length > 10 ? formatMessage(organizationFormMessages.dicError) : null,
    label: formatMessage(organizationMessages.dic),
    onChange: formItemChangeCallback,
    ...formItemServerErrors.dic,
  });

  // ADDRESS
  const street = useInput({ name: 'street', initialValue: data.street ?? '' });
  const city = useInput({ name: 'city', initialValue: data.city ?? '' });
  const postal_code = useInput({
    name: 'postal_code',
    initialValue: data.postal_code ?? '',
  });
  const handleAddressAutocompleteChange = (
    e: React.ChangeEvent,
    changes: { ident: string; value: string }[],
  ) => {
    changes.forEach((change) => {
      switch (change.ident) {
        case 'street': {
          street.onChange(e, change);
          break;
        }
        case 'city': {
          city.onChange(e, change);
          break;
        }
        case 'postal_code': {
          postal_code.onChange(e, change);
          break;
        }
      }
    });
  };

  // NOTES
  const note = useInput<InputProps>({
    name: 'note',
    isDisabled: !acl.canUpdate,
    initialValue: data.note,
    label: formatMessage(organizationMessages.note),
    onChange: formItemChangeCallback,
    ...formItemServerErrors.note,
  });

  const formItems = {
    name,
    ico,
    dic,
    city,
    street,
    postal_code,
    note,
  } as const;

  // ACTIONS
  const handleSave = useCallback(async () => {
    setValidate(true);

    const isSomeFormItemInvalid = Object.values(formItems).some((formItem: any) => {
      return formItem.validator?.(formItem.value);
    });

    if (!isSomeFormItemInvalid) {
      setSaveQueryState(QUERY_STATE.WAITING);

      const formValues = getFormValues(formItems);
      const values = {
        ...formValues,
      };

      try {
        let response;
        if (organizationId) {
          response = await organizationsPartialUpdate({
            query: { id: organizationId },
            request: values,
          });

          if (response.ok) {
            setSaveQueryState(QUERY_STATE.SUCCESS);
          } else {
            setSaveQueryState(QUERY_STATE.FAIL);
          }
        } else {
          response = await organizationsCreate({
            request: values,
          });
          if (response.ok) {
            setSaveQueryState(QUERY_STATE.SUCCESS);

            routeDetail.replace({ id: response.data.id });
          } else {
            setSaveQueryState(QUERY_STATE.FAIL);
          }
        }
      } catch (e) {
        setSaveQueryState(QUERY_STATE.FAIL);
      } finally {
        const organizationsList = organizationsListOperation();
        dispatch(actionResourceInvalidate(organizationsList.path));
      }
    }
    return null;
  }, [name, ico, dic, street, city, postal_code, note]);
  const actionQuery = useMemo(() => ({ id: organizationId }), [organizationId]);
  const { run: doAction, isActionRunning } = useAction({
    query: actionQuery,
    routeList,
    listPath: organizationsListOperation().path,
  });

  const handleDelete = () => {
    if (window.confirm(formatMessage(organizationFormMessages.deleteConfirm))) {
      doAction(organizationsDestroy, setDeleteQueryState, { refetch: false, redirectToList: true });
    }
  };

  return (
    <MainContent width='full' key={organizationId}>
      <article className='sg-u-vs-4'>
        <div className='dmx-doc--head sg-u-vs-2'>
          <div className='sg-a-d-f sg-a-g-2 sg-a-ai-b sg-a-jc-sb sg-a-fw-w'>
            <header className='sg-a-d-f sg-a-ai-c sg-u-hsl-1 sg-a-fw-w'>
              <PageTitle>
                <FormattedMessage
                  {...organizationFormMessages.organizationDetailTitle}
                  values={{ id: data.id || 'empty' }}
                />
              </PageTitle>
            </header>
            <BtnBar>
              {organizationId && acl.canDestroy && (
                <SecondaryBtn
                  isDanger
                  queryState={toBtnQueryState(deleteQueryState)}
                  isDisabled={isActionRunning}
                  onClick={handleDelete}
                >
                  <FormattedMessage {...organizationFormMessages.delete} />
                </SecondaryBtn>
              )}

              {acl.organizationUpdate && (
                <CtaBtn
                  queryState={toBtnQueryState(saveQueryState)}
                  isDisabled={isActionRunning}
                  onClick={handleSave}
                >
                  <FormattedMessage {...organizationFormMessages.save} />
                </CtaBtn>
              )}
            </BtnBar>
          </div>

          {(saveQueryState === QUERY_STATE.WAITING ||
            deleteQueryState === QUERY_STATE.WAITING ||
            resource.queryState === QUERY_STATE.WAITING) && (
            <WaitingAlert>
              <FormattedMessage {...organizationFormMessages.queryStateWaiting} />
            </WaitingAlert>
          )}

          {resource.queryState === QUERY_STATE.FAIL && (
            <DangerAlert>
              <FormattedMessage {...organizationFormMessages.loadQueryStateFail} />
            </DangerAlert>
          )}

          {saveQueryState === QUERY_STATE.FAIL && (
            <DangerAlert>
              <FormattedMessage {...organizationFormMessages.submitQueryStateFail} />
            </DangerAlert>
          )}

          {saveQueryState === QUERY_STATE.SUCCESS && (
            <SuccessAlert>
              <FormattedMessage {...organizationFormMessages.submitQueryStateSuccess} />
            </SuccessAlert>
          )}
        </div>
        <div className='dmx-doc--cols'>
          <div className='sg-u-vs-4'>
            {((resource.queryState === QUERY_STATE.SUCCESS && data.id) || !organizationId) && (
              <Form onSubmit={handleSave}>
                <div className='sg-u-vs-4'>
                  <section>
                    <h3 className='dmx-doc--attributesTitle sg-u-type-h2 sg-a-ml-2'>&nbsp;</h3>
                    <div className='sg-u-box sg-a-p-2 sg-u-vs-2 sg-a-mt-1'>
                      <FormRow>
                        <FormItem {...name}>
                          <Input {...name} />
                        </FormItem>
                      </FormRow>
                      <FormRow>
                        <FormItem {...ico}>
                          <Input {...ico} />
                        </FormItem>
                        <FormItem {...dic}>
                          <Input {...dic} />
                        </FormItem>
                      </FormRow>
                      <AddressAutocomplete
                        streetIdent='street'
                        streetValue={data.street ?? ''}
                        cityIdent='city'
                        cityValue={data.city ?? ''}
                        postalCodeIdent='postal_code'
                        postalCodeValue={data.postal_code ?? ''}
                        onChange={handleAddressAutocompleteChange}
                      />
                      <FormRow>
                        <FormItem {...note}>
                          <Textarea {...note} />
                        </FormItem>
                      </FormRow>
                    </div>
                  </section>
                </div>
              </Form>
            )}
          </div>
        </div>
      </article>
    </MainContent>
  );
});
